import React, { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Add from '@mui/icons-material/Add';
import { Equation, ExpressionType, LogsTemplateField } from '@/__generated__/graphql';
import { keyFromTitle } from '../../utils';
import { EquationCard } from './Equations/EquationCard';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';

interface EquationsConfigTabProps {
  mode: 'ReadOnly' | 'Edit';
  equations: Equation[];
  availableFieldKeys: string[];
  availableFields?: LogsTemplateField[];
  onEquationsChange: (equations: Equation[]) => void;
  onCreateNewField?: (fieldKey: string, equationKey: string) => void;
  originalEquations?: Equation[];
}

export const EquationsConfigTab = ({
  mode,
  equations,
  availableFieldKeys,
  availableFields = [],
  onEquationsChange,
  onCreateNewField,
  originalEquations = [],
}: EquationsConfigTabProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [equationToDeleteIndex, setEquationToDeleteIndex] = useState<number | null>(null);
  const isNewEquation = useCallback(
    (equationKey: string) => {
      return !originalEquations.some((original) => original.key === equationKey);
    },
    [originalEquations]
  );

  const addNewEquation = useCallback(() => {
    const name = 'New Equation';
    const newEquation: Equation = {
      key: keyFromTitle(name),
      name,
      variables: [],
      expression: {
        type: ExpressionType.Number,
        value: 0,
      } as Equation['expression'],
    };
    onEquationsChange([...equations, newEquation]);
  }, [equations, onEquationsChange]);

  const updateEquation = useCallback(
    (index: number, updates: Partial<Equation>) => {
      const updatedEquations = equations.map((equation, i) =>
        i === index ? { ...equation, ...updates } : equation
      );
      onEquationsChange(updatedEquations);
    },
    [equations, onEquationsChange]
  );

  const deleteEquation = useCallback(
    (index: number) => {
      const updatedEquations = equations.filter((_, i) => i !== index);
      onEquationsChange(updatedEquations);
    },
    [equations, onEquationsChange]
  );

  return (
    <Box p={3}>
      <Stack spacing={2}>
        <Typography variant="h6" fontWeight={600}>
          Equations Configuration
        </Typography>

        {mode === 'Edit' && (
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={addNewEquation}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add New Equation
          </Button>
        )}

        {equations.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No equations configured. {mode === 'Edit' && 'Click "Add New Equation" to create one.'}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {equations.map((equation, index) => (
              <EquationCard
                key={`equation-${index}`}
                equation={equation}
                index={index}
                mode={mode}
                availableFieldKeys={availableFieldKeys}
                availableFields={availableFields}
                isNewEquation={isNewEquation(equation.key ?? '')}
                onUpdate={(updates: Partial<Equation>) => updateEquation(index, updates)}
                onDelete={() => {
                  setEquationToDeleteIndex(index);
                  setIsDeleteDialogOpen(true);
                }}
                onCreateNewField={onCreateNewField}
              />
            ))}
          </Stack>
        )}
      </Stack>
      {isDeleteDialogOpen && (
        <GenericDialog
          open={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setEquationToDeleteIndex(null);
          }}
          title="Delete Equation"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this equation? Deleting this equation will cause the
              linked cards to be deleted as well.
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setEquationToDeleteIndex(null);
                }}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (equationToDeleteIndex !== null) {
                    deleteEquation(equationToDeleteIndex);
                    setEquationToDeleteIndex(null);
                    setIsDeleteDialogOpen(false);
                  }
                }}
                sx={containedButtonStyle}
              >
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </Box>
  );
};
